import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useResponsive } from '@/contexts/ResponsiveContext'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useResponsive()
  const { login, isLoading, error, clearError } = useAuthStore()

  const from = location.state?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      username: '',
      password: ''
    }
  })

  useEffect(() => {
    clearError()
  }, [clearError])

  const onSubmit = async (data) => {
    try {
      await login(data)
      toast.success('로그인 성공!')
      navigate(from, { replace: true })
    } catch (error) {
      // Error is already handled by the store and displayed via toast
      console.error('Login error:', error)
    }
  }

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      <div>
        <h2 className={`text-center font-bold text-gray-900 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
          관리자 로그인
        </h2>
        <p className={`mt-2 text-center text-gray-600 ${isMobile ? 'text-sm' : 'text-sm'}`}>
          계정이 없으신가요?{' '}
          <Link
            to="/auth/register"
            className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline transition-colors"
          >
            회원가입
          </Link>
        </p>
      </div>

      <form className={isMobile ? 'space-y-4' : 'space-y-6'} onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="사용자명"
          type="text"
          placeholder="사용자명을 입력하세요"
          autoComplete="username"
          size={isMobile ? 'md' : 'md'}
          error={errors.username?.message}
          {...register('username', {
            required: '사용자명은 필수입니다',
            minLength: {
              value: 3,
              message: '사용자명은 최소 3자 이상이어야 합니다'
            }
          })}
        />

        <Input
          label="비밀번호"
          type="password"
          placeholder="비밀번호를 입력하세요"
          autoComplete="current-password"
          size={isMobile ? 'md' : 'md'}
          showPasswordToggle={true}
          error={errors.password?.message}
          {...register('password', {
            required: '비밀번호는 필수입니다',
            minLength: {
              value: 8,
              message: '비밀번호는 최소 8자 이상이어야 합니다'
            }
          })}
        />

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className={`text-red-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              {error}
            </div>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size={isMobile ? 'lg' : 'md'}
          fullWidth={true}
          loading={isSubmitting || isLoading}
          disabled={isSubmitting || isLoading}
        >
          {isSubmitting || isLoading ? '로그인 중...' : '로그인'}
        </Button>
      </form>

      {/* Demo credentials for development */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`p-4 bg-yellow-50 border border-yellow-200 rounded-md ${isMobile ? 'mt-4' : 'mt-6'}`}>
          <h3 className={`font-medium text-yellow-800 mb-2 ${isMobile ? 'text-sm' : 'text-sm'}`}>
            데모 계정 정보
          </h3>
          <div className={`text-yellow-700 space-y-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
            <p><strong>사용자명:</strong> admin</p>
            <p><strong>비밀번호:</strong> admin123</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginPage